import React, { useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useFocusTrap } from "../../hooks/use-focus-trap";
import "./ConfirmationModal.scss";

interface ConfirmationModalProps {
  isOpen: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  /** Holds the confirm button without the "Processing…" state (e.g. while a pre-check loads). */
  confirmDisabled?: boolean;
  /** Informational popup: a single button labelled confirmText that closes it via onCancel. */
  alertOnly?: boolean;
  onConfirm?: () => void;
  onCancel: () => void;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title: titleProp,
  message,
  confirmText: confirmTextProp,
  cancelText: cancelTextProp,
  loading = false,
  confirmDisabled = false,
  alertOnly = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  // Defaults resolved at render time so they follow the current language;
  // an explicit "" title still hides the heading, as before.
  const title = titleProp ?? t('common.confirm');
  const confirmText = confirmTextProp ?? t('common.yes');
  const cancelText = cancelTextProp ?? t('common.cancel');
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const messageId = useId();

  // Initial focus on Cancel: these confirm destructive actions, so a stray Enter
  // must not trigger one. Escape cancels unless a request is already in flight.
  useFocusTrap(dialogRef, isOpen, {
    initialFocusRef: cancelRef,
    onEscape: () => { if (!loading) onCancel(); },
  });

  if (!isOpen) return null;

  return (
    <div className="confirmation-modal__backdrop">
      <div
        ref={dialogRef}
        className="confirmation-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={messageId}
      >
        {title && <h2 id={titleId} className="confirmation-modal__title">{title}</h2>}
        <div id={messageId} className="confirmation-modal__message">{message}</div>
        <div className="confirmation-modal__actions">
          {alertOnly ? (
            <button
              ref={cancelRef}
              type="button"
              className="confirmation-modal__btn confirmation-modal__btn--confirm"
              onClick={onCancel}
            >
              {confirmText}
            </button>
          ) : (
            <>
              <button
                ref={cancelRef}
                type="button"
                className="confirmation-modal__btn confirmation-modal__btn--cancel"
                onClick={onCancel}
              >
                {cancelText}
              </button>
              <button
                type="button"
                className="confirmation-modal__btn confirmation-modal__btn--confirm"
                onClick={onConfirm}
                disabled={loading || confirmDisabled}
                aria-busy={loading || undefined}
              >
                {loading ? t('common.processingEllipsis') : confirmText}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
